import React, { Component } from "react";
import StepperPage from "./StepperPage";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AuthorisationPage } from "./AuthorisationPage";
import axios from "../../../AxiosClient";
import TreeView from "@material-ui/lab/TreeView";
import TreeItem from "@material-ui/lab/TreeItem";
import shortid from "shortid";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import Alert from "@material-ui/lab/Alert";
import { Trans, withTranslation } from "react-i18next";
import { documentLimitReached } from "../../../utils/LicenceUtils";
import { connect } from "react-redux";

const styles = (theme) => ({
  dryRunContainer: {
    padding: 10,
    textAlign: "center",
  },
  introText: {
    marginBottom: 30,
  },
  labelRoot: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
  },
  labelText: {
    marginRight: 10,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

export class ConfirmPage extends Component {
  interval;

  state = {
    dryRunInProgress: false,
    jobName: "",
    itemsFound: 0,
    dryRunFinished: false,
    showLicenceAlert: false,
    resultTree: {},
    authorisationCode: "",
  };

  componentDidMount() {
    if (!this.props.needsAuth && this.props.show) {
      this.submitJob();
    }
    if (documentLimitReached()) {
      this.props.setReindexDisabled(true);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.show !== prevProps.show) {
      if (!this.props.show) {
        window.clearInterval(this.interval);
      }
      if (this.props.show && !this.props.needsAuth) {
        this.submitJob();
      } else {
        this.setState({
          dryRunFinished: false,
          dryRunInProgress: false,
          resultTree: {},
          jobName: "",
          itemsFound: 0,
        });
      }
    }
  }

  componentWillUnmount() {
    window.clearInterval(this.interval);
  }

  submitJob = () => {
    this.setState({ dryRunInProgress: true });
    axios
      .post("/knowledgesource/dryrun", {
        knowledgeSourceDTO: {
          communityId: 1001,
          url: this.props.url,
          properties: this.props.properties,
          type: this.props.sourceType,
        },
        sourceType: this.props.sourceType,
        authorisationCode: this.state.authorisationCode,
        isDomainAuth: this.props.authOnDomain,
        redirectURL: document.URL.split("/full")[0] + "/oauth2/redirect",
      })
      .then((res) => {
        this.setState({ jobName: res.data.name }, () => {
          this.doPoll();
        });
      });
  };

  doPoll = () => {
    this.interval = window.setInterval(() => {
      axios
        .get(`/knowledgesource/job/status/${this.state.jobName}`)
        .then((res) => {
          if (res.data.jobProgress) {
            this.setState({ itemsFound: res.data.jobProgress.total });
            if (
              this.props.licence.usedDocuments + res.data.jobProgress.total >
              this.props.licence.documents
            ) {
              this.setState({ showLicenceAlert: true });
            }
          }
          if (res.data.triggerState === "NONE") {
            this.setState({
              dryRunFinished: true,
              dryRunInProgress: false,
              resultTree: res.data.jobProgress
                ? res.data.jobProgress.resultTree
                : {},
            });
            window.clearInterval(this.interval);
          }
        });
    }, 2000);
  };

  render() {
    const { classes } = this.props;
    return (
      <div className={classes.dryRunContainer}>
        {this.props.needsAuth &&
          !this.state.dryRunInProgress &&
          !this.state.dryRunFinished && (
            <>
              <AuthorisationPage
                isDryRun
                onAuth={this.onAuth}
                sourceType={this.props.sourceType}
                authOnDomain={this.props.authOnDomain}
                clientId={this.props.clientId}
              />
            </>
          )}
        {this.state.dryRunInProgress && (
          <>
            {this.state.showLicenceAlert && (
              <Alert
                severity="warning"
                style={{ margin: "0 auto 15px auto", width: "50%" }}
              >
                <Trans i18nKey="create_ks_dry_run_exceed_document_limit">
                  create_ks_dry_run_exceed_document_limit
                </Trans>
              </Alert>
            )}
            <Typography className={classes.introText}>
              <Trans i18nKey="create_ks_dry_run_in_progress" />
            </Typography>
            <CircularProgress size={150} style={{ marginBottom: 10 }} />
            <Typography>
              {this.props.t("dry_run_found_items_for_indexing_in_progress", {
                count: this.state.itemsFound,
              })}
            </Typography>
          </>
        )}
        {this.state.dryRunFinished && (
          <>
            {this.state.showLicenceAlert && (
              <Alert
                severity="warning"
                style={{ margin: "0 auto 15px auto", width: "50%" }}
              >
                <Trans i18nKey="create_ks_dry_run_exceed_document_limit">
                  create_ks_dry_run_exceed_document_limit
                </Trans>
              </Alert>
            )}
            <Typography>
              {this.props.t("dry_run_found_items_for_indexing", {
                count: this.state.itemsFound,
              })}
            </Typography>
            <TreeView
              className={classes.root}
              defaultCollapseIcon={<ArrowDropDownIcon />}
              defaultExpandIcon={<ArrowRightIcon />}
              defaultEndIcon={<div style={{ width: 24 }} />}
            >
              {this.renderChildrenForNode(this.state.resultTree, 1)}
            </TreeView>
          </>
        )}
      </div>
    );
  }

  onAuth = (authorisationCode) => {
    this.setState({ authorisationCode }, () => {
      this.submitJob();
    });
  };

  renderChildrenForNode = (node, id) => {
    if (!node.children) return;
    return node.children.map((item, idx) => {
      return (
        <TreeItem
          nodeId={shortid.generate()}
          label={
            <div className={this.props.classes.labelRoot}>
              <Typography
                variant="body2"
                className={this.props.classes.labelText}
              >
                {item.data}
              </Typography>
              {item.children.length > 0 && (
                <Typography variant="caption">
                  {item.children.length} <Trans i18nKey="global_item_plural" />
                </Typography>
              )}
            </div>
          }
        >
          {this.renderChildrenForNode(item, id + 1)}
        </TreeItem>
      );
    });
  };
}

const mapStateToProps = (state) => ({
  licence: state.setting.licence,
});
export default connect(
  mapStateToProps,
  null
)(StepperPage(withStyles(styles)(withTranslation()(ConfirmPage))));
